// @flow

import type {
  Loading,
  Loaded,
  Items,
  State,
  RouteState,
  RouteItem,
  Slug,
  ActiveItem,
  PdbId,
} from './types';
import type { State as RouterState } from '../router';
import type { FlatError } from '../../helpers/flattenError';
import type { ACFPage, ACFImage } from '../../types';
import type { Yoast } from '../../types/apiRoute';

import { initialState } from './reducers/route';
import { getSlug } from '../router/selectors';
import {
  route,
} from './reducers';

type GlobalState = {
  router: RouterState,
  routes: State,
};

// We assume this reducer is included as 'routes' in state
const getReducer = (state: GlobalState): State => state.routes;

export const getActiveItem = (state: GlobalState): ActiveItem => (
  getReducer(state).activeItem
);

export const getItems = (state: GlobalState): Items => (
  getReducer(state).items
);

export const getItem = (state: GlobalState, slug: ?Slug = null): RouteState => {
  const path = slug || getSlug(state);
  return getItems(state)[path] || route(initialState, { type: 'NULL' });
};

export const isLoading = (state: GlobalState, slug: ?Slug = null): Loading => (
  !!getItem(state, slug).loading
);

export const isLoaded = (state: GlobalState, slug: ?Slug = null): Loaded => (
  !!getItem(state, slug).loaded
);

export const hasError = (state: GlobalState, slug: ?Slug = null): boolean => (
  !!getItem(state, slug).error
);

export const getError = (state: GlobalState, slug: ?Slug = null): ?FlatError => (
  getItem(state, slug).error
);

export const needsLoading = (state: GlobalState, slug: ?Slug = null): boolean => (
  !isLoaded(state, slug)
  && !hasError(state, slug)
  && !isLoading(state, slug)
);

export const getRoute = (state: GlobalState, slug: ?Slug = null): RouteItem => (
  getItem(state, slug).item
);

export const getActiveRoute = (state: GlobalState): ?RouteItem => {
  const { activeItem } = getReducer(state);
  if (activeItem === null) {
    return null;
  }

  return getItem(state, activeItem).item;
};

export const getId = (state: GlobalState): number => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute) {
    throw new Error('getId() needs activeRoute');
  }

  return activeRoute.id;
};

export const getTemplate = (state: GlobalState): string => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute) {
    throw new Error('getTemplate() needs activeRoute');
  }

  return activeRoute.template;
};

export const getType = (state: GlobalState): string => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute) {
    throw new Error('getType() needs activeRoute');
  }

  return activeRoute.type;
};

export const getYoast = (state: GlobalState): ?Yoast => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute || !activeRoute.yoast) {
    return null;
  }

  return activeRoute.yoast;
};

export const getNewsArticleDate = (state: GlobalState): ?string => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute || !activeRoute.date) {
    return null;
  }

  return activeRoute.date;
};

export const getNewsArticleTitle = (state: GlobalState): ?string => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute || !activeRoute.title || !activeRoute.title.rendered) {
    return null;
  }

  return activeRoute.title.rendered;
};

export const getNewsArticleContent = (state: GlobalState): string => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute || !activeRoute.content || !activeRoute.content.rendered) {
    return '';
  }

  return activeRoute.content.rendered;
};

export const getNewsArticleImage = (state: GlobalState): ?ACFImage => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute || !activeRoute.image || !activeRoute.image) {
    return null;
  }

  return activeRoute.image;
};

export const getFAQ = (state: GlobalState) => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute || !activeRoute.faq) {
    return null;
  }

  return activeRoute.faq;
};

export const getFAQCategory = (state: GlobalState) => {
  const faqData = getFAQ(state);
  return (faqData && faqData.categories) ? faqData.categories : null;
};

export const getFAQItem = (state: GlobalState) => {
  const faqData = getFAQ(state);
  return (faqData && faqData.item) ? faqData.item : null;
};

export const getFAQParent = (state: GlobalState) => {
  const faqData = getFAQ(state);
  return (faqData && faqData.parent) ? faqData.parent : null;
};

export const getFAQResults = (state: GlobalState) => {
  const faqData = getFAQ(state);
  return (faqData && faqData.result) ? faqData.result : null;
};

export const getFAQResultsQuery = (state: GlobalState) => {
  const faqResults = getFAQResults(state);
  return (faqResults && faqResults.query) ? faqResults.query : null;
};

export const getFAQResultsItems = (state: GlobalState) => {
  const faqResults = getFAQResults(state);
  return (faqResults && faqResults.items) ? faqResults.items : null;
};

export const getAcf = (state: GlobalState): ?ACFPage => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute || !activeRoute.acf) {
    return null;
  }

  return activeRoute.acf;
};

export const getTheme = (state: GlobalState): ?string => {
  const acf = getAcf(state);
  if (!acf) {
    return null;
  }
  return (acf: ACFPage).pagebuilder_theme;
};

export const getDisableComparer = (state: GlobalState): ?boolean => {
  const activeRoute = getActiveRoute(state);
  if (!activeRoute) return false;
  return activeRoute.disableComparer;
};

export const getSaveCompositionForm = (state: GlobalState): ?string => {
  const acf = getAcf(state);
  if (!acf) {
    return null;
  }
  return (acf: ACFPage).form_data;
};

export const getPdbIdFromSlug = (state: GlobalState, slug: Slug): ?PdbId => {
  const item = getItem(state, slug);
  return item.pdbIds[0];
};

export const getActiveRoutePdbIds = (state: GlobalState): ?Array<PdbId> => {
  const { activeItem } = getReducer(state);
  if (activeItem === null) {
    return null;
  }
  return getItem(state, activeItem).pdbIds;
};

export const getActiveRoutePdbId = (state: GlobalState): ?PdbId => {
  const ids = getActiveRoutePdbIds(state);
  if (!ids) {
    return null;
  }
  return ids.length ? ids[0] : null;
};

export const getBusinessLabel = (state: GlobalState): string => {
  const { item } = getItem(state);
  if (!item.acf || !item.acf.business_label_settings) {
    return '';
  }
  return item.acf.business_label_settings;
};

export const getAvailableFilters = (state: GlobalState): ?Array<string> => {
  const acf = getAcf(state);
  if (!acf) {
    return null;
  }

  return (acf: ACFPage).available_filters;
};
